/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";

/*
  Android System WebView versions can lack Promise.withResolvers().
  Recent PDF.js builds use it internally, so provide the standards-compatible
  helper before PDF.js is loaded.
*/
if (typeof Promise.withResolvers !== "function") {
  Promise.withResolvers = function withResolvers() {
    let resolve;
    let reject;

    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    return { promise, resolve, reject };
  };
}

let pdfJsPromise = null;

async function loadPdfJs() {
  if (!pdfJsPromise) {
    pdfJsPromise = import("pdfjs-dist/legacy/build/pdf.mjs").then((module) => {
      const pdfjsLib = module?.default || module;

      /*
        Keep the worker matched to the installed PDF.js version.
        The Promise.withResolvers polyfill above is installed before either the
        main library or worker is requested.
      */
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

      return pdfjsLib;
    });
  }

  return pdfJsPromise;
}

export default function PdfPage({ url, title = "PDF preview", onClose }) {
  const pagesRef = useRef(null);
  const renderTokenRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    if (!url || !pagesRef.current) return undefined;

    const token = ++renderTokenRef.current;
    let cancelled = false;
    let loadingTask = null;

    async function renderPdf() {
      setLoading(true);
      setError("");
      setPageCount(0);

      const host = pagesRef.current;
      host.innerHTML = "";

      try {
        const pdfjsLib = await loadPdfJs();

        if (cancelled || token !== renderTokenRef.current) return;

        loadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: false,
          disableAutoFetch: false,
          disableStream: false,
          useWorkerFetch: true,
        });

        const pdf = await loadingTask.promise;

        if (cancelled || token !== renderTokenRef.current) return;

        setPageCount(pdf.numPages);

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled || token !== renderTokenRef.current) return;

          const page = await pdf.getPage(pageNumber);

          const availableWidth = Math.max(
            280,
            Math.min(host.clientWidth - 24, 900)
          );

          const baseViewport = page.getViewport({ scale: 1 });
          const scale = availableWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });

          const pageShell = document.createElement("div");
          pageShell.className = "pp-pdf-preview-page";

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { alpha: false });

          if (!context) {
            throw new Error("Canvas rendering is unavailable on this device.");
          }

          const outputScale = Math.min(window.devicePixelRatio || 1, 2);

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;

          pageShell.appendChild(canvas);
          host.appendChild(pageShell);

          await page.render({
            canvasContext: context,
            viewport,
            transform:
              outputScale === 1
                ? null
                : [outputScale, 0, 0, outputScale, 0, 0],
          }).promise;
        }
      } catch (err) {
        console.error("PresentPlus PDF preview failed:", err);

        if (!cancelled) {
          const message = String(err?.message || "").toLowerCase();

          if (
            message.includes("failed to fetch") ||
            message.includes("cors") ||
            message.includes("worker")
          ) {
            setError(
              "The secure report could not be loaded for preview. Please check your connection and try again."
            );
          } else {
            setError(
              "The report could not be previewed inside the app. You can still use Download PDF."
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    renderPdf();

    return () => {
      cancelled = true;
      renderTokenRef.current += 1;

      try {
        loadingTask?.destroy?.();
      } catch {}
    };
  }, [url]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div className="pp-pdf-preview-overlay" role="dialog" aria-modal="true">
      <style>{styles}</style>

      <div className="pp-pdf-preview-modal">
        <header className="pp-pdf-preview-header">
          <div>
            <div className="pp-pdf-preview-eyebrow">PresentPlus report</div>
            <h2>{title}</h2>

            {pageCount ? (
              <div className="pp-pdf-preview-count">
                {pageCount} {pageCount === 1 ? "page" : "pages"}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className="pp-pdf-preview-close"
            onClick={onClose}
            aria-label="Close PDF preview"
          >
            ×
          </button>
        </header>

        <div className="pp-pdf-preview-body">
          {loading ? (
            <div className="pp-pdf-preview-state">
              <div className="pp-pdf-spinner" />
              <strong>Preparing secure preview…</strong>
              <span>This may take a few seconds.</span>
            </div>
          ) : null}

          {error ? (
            <div className="pp-pdf-preview-error">{error}</div>
          ) : null}

          <div
            ref={pagesRef}
            className="pp-pdf-preview-pages"
            aria-label="PDF pages"
          />
        </div>

        <footer className="pp-pdf-preview-footer">
          <button type="button" onClick={onClose}>
            Close preview
          </button>
        </footer>
      </div>
    </div>
  );
}

const styles = `
  .pp-pdf-preview-overlay,
  .pp-pdf-preview-overlay * {
    box-sizing: border-box;
  }

  .pp-pdf-preview-overlay {
    position: fixed;
    inset: 0;
    z-index: 100000;
    padding:
      calc(12px + env(safe-area-inset-top, 0px))
      12px
      calc(12px + env(safe-area-inset-bottom, 0px));
    display: grid;
    place-items: center;
    background: rgba(7, 22, 51, 0.76);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .pp-pdf-preview-modal {
    width: min(960px, 100%);
    height: min(92dvh, 980px);
    min-height: 0;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    overflow: hidden;
    border-radius: 26px;
    background: #eef4ff;
    border: 1px solid rgba(255,255,255,0.72);
    box-shadow: 0 30px 100px rgba(0,0,0,0.34);
  }

  .pp-pdf-preview-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 18px;
    color: #ffffff;
    background: linear-gradient(135deg, #1559e8, #4f46e5);
  }

  .pp-pdf-preview-header h2 {
    margin: 5px 0 0;
    font-size: clamp(19px, 4vw, 26px);
    line-height: 1.1;
  }

  .pp-pdf-preview-eyebrow {
    font-size: 10px;
    font-weight: 950;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    opacity: 0.78;
  }

  .pp-pdf-preview-count {
    margin-top: 7px;
    font-size: 12px;
    font-weight: 800;
    opacity: 0.8;
  }

  .pp-pdf-preview-close {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255,255,255,0.24);
    border-radius: 14px;
    color: #ffffff;
    background: rgba(255,255,255,0.16);
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
  }

  .pp-pdf-preview-body {
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 12px;
  }

  .pp-pdf-preview-pages {
    width: 100%;
    display: grid;
    justify-items: center;
    gap: 14px;
  }

  .pp-pdf-preview-page {
    max-width: 100%;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(15,23,42,0.16);
  }

  .pp-pdf-preview-page canvas {
    display: block;
    max-width: 100%;
    height: auto !important;
  }

  .pp-pdf-preview-state {
    min-height: 220px;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 10px;
    color: #183566;
    text-align: center;
  }

  .pp-pdf-preview-state span {
    color: #64748b;
    font-size: 13px;
  }

  .pp-pdf-spinner {
    width: 34px;
    height: 34px;
    border: 4px solid #dbe7ff;
    border-top-color: #2563eb;
    border-radius: 999px;
    animation: ppPdfSpin 0.8s linear infinite;
  }

  .pp-pdf-preview-error {
    margin: 12px;
    padding: 14px;
    border-radius: 16px;
    color: #9f1239;
    background: #fff1f2;
    border: 1px solid #fecdd3;
    font-weight: 800;
    line-height: 1.45;
  }

  .pp-pdf-preview-footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    background: #ffffff;
    border-top: 1px solid #dbe4f3;
  }

  .pp-pdf-preview-footer button {
    min-height: 44px;
    padding: 0 18px;
    border: 0;
    border-radius: 14px;
    color: #ffffff;
    background: #2563eb;
    font-weight: 900;
  }

  @keyframes ppPdfSpin {
    to { transform: rotate(360deg); }
  }
`;
